import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { Link as GatsbyLink } from 'gatsby';

import useUserContext from '../hooks/use-user-context';
import {
  isInternal,
  isRelative,
  convertAbsoluteToRelative,
  generateLanguagePath,
  isKnownDomain,
  isTargetBlankDomain,
  isEuReplaceDomain,
  absolutizeCDNUrls,
  convertPdfCDNUrls
} from '../utilities/url';
import { removeNoTranslateWrapper } from '../utilities/misc';
import { DEFAULT_LANGUAGE } from '../constants';
import VariableText from './VariableText';
import LinkQueryParams from './LinkQueryParams';

const linkVariableRe = /{{link-([a-z0-9-_.]+)}}/i;

const Link = forwardRef(({
  to = '',
  targetBlank = false,
  children,
  className,
  id,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  partiallyActive,
  activeStyle,
  itemProp,
  itemScope,
  itemType,
  'aria-current': ariaCurrent,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}, ref) => {
  const isLinkVariable = linkVariableRe.test(to);

  if (isLinkVariable) {
    const variableName = to.trim().replace(linkVariableRe, '$1');

    return (
      <VariableText
        type="link"
        name={variableName}
        render={(value) => (
          <LinkInner
            ref={ref}
            to={value}
            targetBlank={targetBlank}
            className={className}
            id={id}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role={role}
            tabIndex={tabIndex}
            itemProp={itemProp}
            itemScope={itemScope}
            itemType={itemType}
            aria-current={ariaCurrent}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
          >
            {children}
          </LinkInner>
        )}
      />
    );
  }

  return (
    <LinkInner
      ref={ref}
      to={to}
      targetBlank={targetBlank}
      className={className}
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      partiallyActive={partiallyActive}
      activeStyle={activeStyle}
      itemProp={itemProp}
      itemScope={itemScope}
      itemType={itemType}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </LinkInner>
  );
});

const LinkInner = forwardRef(({
  to = '',
  targetBlank = false,
  children,
  className,
  id,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  partiallyActive,
  activeStyle,
  itemProp,
  itemScope,
  itemType,
  'aria-current': ariaCurrent,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}, ref) => {
  const { language, region } = useUserContext();
  const { pathname, href } = useLocation();
  let toFormatted = removeNoTranslateWrapper(to);
  toFormatted = absolutizeCDNUrls(toFormatted);
  toFormatted = convertPdfCDNUrls(toFormatted, language);
  const isRelativeLink = isRelative(toFormatted);
  const isInternalLink = isRelativeLink || isInternal(toFormatted);
  const isKnownDomainLink = !isInternalLink && isKnownDomain(toFormatted);
  const isEuReplaceDomainLink = isKnownDomainLink && isEuReplaceDomain(toFormatted);
  const shouldOpenNewWindow = targetBlank
    || (!isInternalLink && isTargetBlankDomain(toFormatted))
    || (!isInternalLink && !isKnownDomainLink);
  const notEmpty = toFormatted !== '' && toFormatted !== null;
  const isAnchorLink = notEmpty && toFormatted.startsWith('#');

  if (notEmpty && isInternalLink && !isRelativeLink) {
    toFormatted = convertAbsoluteToRelative(toFormatted);
  }

  const isApiLink = notEmpty && toFormatted.startsWith('/api/');
  const isSpecialInternalLink = isInternalLink
    && toFormatted
    && (toFormatted.startsWith('/my/') || toFormatted.startsWith('/trial/') || toFormatted.startsWith('/form/'));
  const shouldAddQueryParams = isKnownDomainLink || isSpecialInternalLink;
  const isBookingRedirect = isInternalLink
    && toFormatted
    && toFormatted.startsWith('/bookings/');

  if (notEmpty && !isAnchorLink && !isApiLink && isInternalLink && language !== DEFAULT_LANGUAGE) {
    toFormatted = generateLanguagePath(language, toFormatted);
  }

  if (isAnchorLink) {
    toFormatted = `${pathname}${toFormatted}`;
  }

  const [link, setLink] = useState(toFormatted);

  useEffect(() => {
    let toPath = toFormatted;

    if (!notEmpty) {
      return;
    }

    if (isEuReplaceDomainLink && region === 'eu') {
      toPath = toPath.replace('splashtop.com', 'splashtop.eu');
    }

    setLink(toPath);
  }, [
    toFormatted,
    notEmpty,
    region,
    isEuReplaceDomainLink
  ]);

  if (toFormatted === '' || toFormatted === null) {
    return (
      <span
        ref={ref}
        className={className}
        id={id}
        role={role}
        tabIndex={tabIndex}
        itemProp={itemProp}
        itemScope={itemScope}
        itemType={itemType}
        aria-current={ariaCurrent}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {children}
      </span>
    );
  }

  if (
    !targetBlank
    && !isBookingRedirect
    && !isApiLink
    && (isInternalLink && !isSpecialInternalLink)
  ) {
    return (
      <GatsbyLink
        ref={ref}
        to={link}
        className={className}
        id={id}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDown}
        partiallyActive={partiallyActive}
        activeStyle={activeStyle}
        itemProp={itemProp}
        itemScope={itemScope}
        itemType={itemType}
        aria-current={ariaCurrent}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        state={{ prevUrl: href }}
      >
        {children}
      </GatsbyLink>
    );
  }

  let LinkComponent = 'a';

  if (shouldAddQueryParams) {
    LinkComponent = LinkQueryParams;
  }

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <LinkComponent
      ref={ref}
      href={link}
      target={shouldOpenNewWindow ? '_blank' : null}
      className={className}
      id={id}
      rel={shouldOpenNewWindow ? 'noopener noreferrer' : null}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      itemProp={itemProp}
      itemScope={itemScope}
      itemType={itemType}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </LinkComponent>
  );
});

Link.propTypes = {
  to: PropTypes.string,
  targetBlank: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  role: PropTypes.string,
  tabIndex: PropTypes.number,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  itemProp: PropTypes.string,
  itemScope: PropTypes.bool,
  itemType: PropTypes.string,
  'aria-current': PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string
};

export default Link;
